import { JobseekerService } from '../shared/services/jobseeker.service';
import { JobseekerWorkExperienceCategoryModel } from '../shared/models/jobseeker-work-experience-category';
import { ConfigurationService } from '../../shared/services/configuration.service';
import { LookupService } from '../../shared/services/lookup.service';
import { CommonLookupItem } from '../../shared/models/common-lookup-item.model';
import { FieldState } from '../../shared/models/field-state';
import { JobseekerWorkexperienceModel } from '../shared/models/jobseeker-workexperience-model';
import {
  Component,
  OnInit,
  Input,
  OnChanges,
  SimpleChanges,
  Inject,
  EventEmitter,
  Output,
  NgZone,
} from '@angular/core';
import { Utils } from '@admin/shared/utils';
import { OccupationCategory } from '@admin/shared/models/occupation-category.model';
import { Validator, IValidator } from '@admin/shared/validation/validators';
import { ValidationUtils } from '@admin/shared/validation/validation-runner';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'hr-work-experience-editor',
  templateUrl: './work-experience-editor.component.html',
  styleUrls: ['./work-experience-editor.component.less'],
  standalone: false,
})
export class WorkExperienceEditorComponent implements OnInit, OnChanges {
  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['workExperienceInput'] &&
      changes['workExperienceInput'].currentValue != null
    ) {
      this.workExperience = Utils.cloneObject(
        this.workExperienceInput,
        JobseekerWorkexperienceModel
      );
      this.initializeFieldsState();
    }
  }

  @Output()
  public onUpdate: EventEmitter<boolean> = new EventEmitter<boolean>();

  @Output()
  public onCancelEdit: EventEmitter<boolean> = new EventEmitter<boolean>();

  @Input('workExperience')
  public workExperienceInput: JobseekerWorkexperienceModel;

  public workExperience: JobseekerWorkexperienceModel;

  public monthListFull: CommonLookupItem<number, string>[];

  public yearList: CommonLookupItem<number, string>[];

  public categoryOptions: { label: string; value: OccupationCategory }[];

  public selectedCategories: OccupationCategory[];

  public maxCategoriesAllowed: number;

  public maxCategoriesExceeded: boolean;

  public occupationCategoryDropDownPlaceholderText: string;

  public selectedFileName: string;

  private fieldStates: {
    dateFromMonth: FieldState;
    dateFromYear: FieldState;
    dateToMonth: FieldState;
    dateToYear: FieldState;
    employer: FieldState;
    positionTitle: FieldState;
    categories: FieldState;
    description: FieldState;
  };

  public get field() {
    return this.fieldStates;
  }

  public beforeUpload() {}

  public onWorkExperienceCategoryOptionRemoveClick(workExperienceCategory) {
    this.selectedCategories = this.selectedCategories.filter(
      (c) =>
        c.occupationCategoryId != workExperienceCategory.occupationCategoryId
    );

    if (this.selectedCategories.length < this.maxCategoriesAllowed) {
      this.maxCategoriesExceeded = false;
      return;
    }
  }

  public onCurrentWorkChange() {
    let workExperience = this.workExperience;

    if (workExperience.isCurrent) {
      workExperience.dateToYear = null;
      workExperience.dateToMonth = null;
    }
  }

  onSaveWorkExperienceClick() {
    this.workExperience.categories = this.selectedCategories.map(
      (c) =>
        <JobseekerWorkExperienceCategoryModel>{
          jobseekerWorkExperienceId:
            this.workExperience.jobseekerWorkExperienceId,
          category: c,
          jobseekerWorkExperienceCategoryId: -1,
        }
    );
    var validators = this.getValidators();

    this.invokeValidation(validators, () => {
      this.jobseekerService
        .saveJobseekerWorkExperience(this.workExperience, this.selectedFileName)
        .subscribe(() => {
          this.onUpdate.emit(true);
        });

      // this.httpPost('profile/workexperience', { "Field": Field[Field.WorkExperience], "Value": JSON.stringify({ WorkExperience: workExperience }) }, {})
      //     .then(data => {
      //         window.location.reload(true);
      //     });
    });
  }

  public onCancelAddWorkExperienceClick() {
    this.onCancelEdit.emit(true);
  }

  invokeValidation(validators: IValidator[], onValid?: () => void) {
    ValidationUtils.invokeValidation(
      validators,
      this.getFieldStatesObject(),
      onValid
    );
  }

  private getFieldStatesObject(): { [index: string]: FieldState } {
    let result: { [index: string]: FieldState } = {};
    for (let p in this.fieldStates) {
      if (this.fieldStates.hasOwnProperty(p)) {
        result[this.fieldStates[p].fieldId] = this.fieldStates[p];
      }
    }

    return result;
  }

  private getValidators() {
    let jobseekerWorkExperience = this.workExperience;

    let emptyMessage = this.translationService.instant(
      'message.ValueLeftEmpty'
    );
    let dateMessage = this.translationService.instant(
      'message.StartDateMustNotComeAfterEndDate'
    );

    let field = this.field;

    let dateFromMonth = jobseekerWorkExperience.dateFromMonth;
    let dateToMonth = jobseekerWorkExperience.dateToMonth;
    var validators = [
      Validator.forRequired(
        field.dateFromYear,
        () => jobseekerWorkExperience.dateFromYear,
        emptyMessage
      ),
      Validator.forRequired(
        field.employer,
        () => jobseekerWorkExperience.employer,
        emptyMessage
      ),
      Validator.forRequired(
        field.positionTitle,
        () => jobseekerWorkExperience.positionTitle,
        emptyMessage
      ),
      Validator.forPredicate(
        field.categories,
        () => jobseekerWorkExperience.categories.some((c) => c != null),
        emptyMessage
      ),
    ];

    if (!jobseekerWorkExperience.isCurrent) {
      validators.push(
        ...[
          Validator.forRequired(
            field.dateToYear,
            () => jobseekerWorkExperience.dateToYear,
            emptyMessage
          ),
          Validator.forPredicate(
            field.dateToYear,
            () => {
              var t =
                +jobseekerWorkExperience.dateToYear >=
                +jobseekerWorkExperience.dateFromYear;
              return t;
            },
            dateMessage
          ),
          Validator.forPredicate(
            field.dateToMonth,
            () =>
              dateFromMonth == null ||
              dateToMonth == null ||
              jobseekerWorkExperience.dateFromYear == null ||
              jobseekerWorkExperience.dateToYear == null ||
              jobseekerWorkExperience.dateFromYear !=
                jobseekerWorkExperience.dateToYear ||
              +dateToMonth >= +dateFromMonth,
            dateMessage
          ),
        ]
      );
    }
    return validators;
  }

  public onDeleteWorkExperienceAttachmentClick() {
    this.workExperience.attachment = null;
  }

  // these two methods below are workaround for primeng MultiSelect to disable selecting head category
  onCategoryClick(category: OccupationCategory) {
    if (category.isHeadOld()) {
      event.stopPropagation();
    }
  }
  onCategoryFilterChange(e) {
    if (e.itemValue.isHeadOld() || e.value.length > this.maxCategoriesAllowed) {
      this.onWorkExperienceCategoryOptionRemoveClick(e.itemValue);
      if (e.value.length > this.maxCategoriesAllowed) {
        this.maxCategoriesExceeded = true;
      }
    }
  }

  private initializeFieldsState() {
    this.fieldStates = {
      dateFromMonth: new FieldState(() => this.workExperience.dateFromMonth),
      dateFromYear: new FieldState(() => this.workExperience.dateFromYear),
      dateToMonth: new FieldState(() => this.workExperience.dateToMonth),
      dateToYear: new FieldState(() => this.workExperience.dateToYear),
      employer: new FieldState(() => this.workExperience.employer),
      positionTitle: new FieldState(() => this.workExperience.positionTitle),
      categories: new FieldState(() => this.workExperience.categories),
      description: new FieldState(() => this.fieldStates.description),
    };
  }

  constructor(
    private lookupService: LookupService,
    private configService: ConfigurationService,
    private translationService: TranslateService,
    private jobseekerService: JobseekerService
  ) {
    this.selectedCategories = [];
  }

  ngOnInit() {
    let lookupData = this.lookupService.getCachedLookupData();
    this.monthListFull = lookupData.monthListFull;
    this.yearList = lookupData.yearList;

    this.categoryOptions = lookupData.occupationCategories
      .sort((a, b) => {
        const headsDiff = a.headId - b.headId;
        if (headsDiff != 0) {
          return headsDiff;
        }
        return a.isHeadOld() ? -1 : 1;
      })
      .map((c) => ({ label: c.name, value: c }));

    if (this.workExperience.jobseekerWorkExperienceId > 0) {
      this.selectedCategories = this.workExperience.categories.map(
        (c) =>
          this.categoryOptions.filter(
            (co) =>
              co.value.occupationCategoryId === c.category.occupationCategoryId
          )[0].value
      );
    }

    this.maxCategoriesAllowed =
      this.configService.getCachedConfig().workExperienceMaxCategoriesAllowed;
  }
}
